import { AlertCircle } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  EVENT_REMINDER_DAY_MAX_VALUE,
  EVENT_REMINDER_HOUR_MAX_VALUE,
  EVENT_REMINDER_MINUTE_MAX_VALUE,
  UNIT_DAY,
  UNIT_HOUR,
  UNIT_MINUTE,
} from "@/constants";
import type { Reminder } from "@/lib/humhub-api/types";
import { checkDuplicateReminder } from "@/lib/utils/reminderValidation";

interface NewReminderFormProps {
  existingReminders: Reminder[];
  onSave: (reminder: { unit: number; value: number }) => void;
  onCancel: () => void;
}

export const NewReminderForm = ({
  existingReminders,
  onSave,
  onCancel,
}: NewReminderFormProps) => {
  const { t } = useTranslation();
  const [unit, setUnit] = useState<number>(UNIT_DAY);
  const [value, setValue] = useState(1);
  const [inputValue, setInputValue] = useState(String(1));
  const [validationError, setValidationError] = useState<string | null>(null);

  const getMaxValueForUnit = (unitType: number): number => {
    switch (unitType) {
      case UNIT_MINUTE:
        return EVENT_REMINDER_MINUTE_MAX_VALUE;
      case UNIT_HOUR:
        return EVENT_REMINDER_HOUR_MAX_VALUE;
      case UNIT_DAY:
        return EVENT_REMINDER_DAY_MAX_VALUE;
      default:
        return 999;
    }
  };

  const handleValueChange = (newValue: number) => {
    setValue(newValue);
    setInputValue(String(newValue));
    const validation = checkDuplicateReminder(
      { unit, value: newValue },
      existingReminders,
      null,
      t,
    );
    setValidationError(validation.error ?? null);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };

  const handleInputBlur = () => {
    const rawValue = Number.parseInt(inputValue, 10);
    const maxValue = getMaxValueForUnit(unit);
    const sanitizedValue = Number.isNaN(rawValue)
      ? value
      : Math.max(1, Math.min(rawValue, maxValue));
    handleValueChange(sanitizedValue);
  };

  const handleUnitChange = (newUnit: string) => {
    const unitNum = Number.parseInt(newUnit, 10);
    setUnit(unitNum);
    const maxValue = getMaxValueForUnit(unitNum);
    const sanitized = Math.max(1, Math.min(value, maxValue));
    setValue(sanitized);
    setInputValue(String(sanitized));
    const validation = checkDuplicateReminder(
      { unit: unitNum, value: sanitized },
      existingReminders,
      null,
      t,
    );
    setValidationError(validation.error ?? null);
  };

  const handleSave = () => {
    // Final validation
    const validation = checkDuplicateReminder(
      { unit, value },
      existingReminders,
      null,
      t,
    );

    if (!validation.isValid) {
      setValidationError(validation.error ?? "");
      return;
    }

    onSave({
      unit,
      value,
    });
  };

  const maxValue = getMaxValueForUnit(unit);
  const formatUnitLabel = (unitType: number) => {
    if (unitType === UNIT_MINUTE) return t("common.minute", { count: value });
    if (unitType === UNIT_HOUR) return t("common.hour", { count: value });
    if (unitType === UNIT_DAY) return t("common.day", { count: value });
    return "";
  };

  return (
    <div className="space-y-4">
      {/* Time Setting */}
      <div className="space-y-2">
        <Label>{t("eventManage.reminderTimeLabel")}</Label>
        <div className="flex items-center gap-2">
          <Input
            type="number"
            min={1}
            max={maxValue}
            value={inputValue}
            onChange={handleInputChange}
            onBlur={handleInputBlur}
            className={validationError ? "w-20 border-red-500" : "w-20"}
          />
          <Select value={String(unit)} onValueChange={handleUnitChange}>
            <SelectTrigger className="w-[120px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={String(UNIT_MINUTE)}>
                {formatUnitLabel(UNIT_MINUTE)}
              </SelectItem>
              <SelectItem value={String(UNIT_HOUR)}>
                {formatUnitLabel(UNIT_HOUR)}
              </SelectItem>
              <SelectItem value={String(UNIT_DAY)}>
                {formatUnitLabel(UNIT_DAY)}
              </SelectItem>
            </SelectContent>
          </Select>
          <span className="text-sm text-gray-600">
            {t("eventManage.beforeEvent")}
          </span>
        </div>

        {/* Validation Error */}
        {validationError && (
          <div className="flex items-center gap-2 text-sm text-red-600">
            <AlertCircle className="size-4" />
            <span>{validationError}</span>
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div className="flex gap-2 border-t pt-4">
        <Button
          variant="teal"
          onClick={handleSave}
          disabled={!!validationError}
        >
          {t("eventManage.createAndSave")}
        </Button>
        <Button variant="outline" onClick={onCancel}>
          {t("common.cancel")}
        </Button>
      </div>
    </div>
  );
};
